import downloadFile from 'downloadjs';
import { get as _get } from 'lodash';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { encodeFileNameInZipArchive, sortDeliverables } from 'utils/deliverables';
import { logError } from 'utils/log';
import { API, get, post } from 'utils/api';
import { getDisplayDate } from 'utils/date';
import { getCloudinaryImageUrl } from 'utils/cloudinary';
import { isAccepted, isNegotiationsReopened } from 'utils/subcampaign';
import { getFormattedFollowersValue, getNumFollowers } from 'utils/influencers';
import { getCampaignAssetsPreviews } from 'utils/campaignAsset';
import { getFormattedPercentageRate } from 'utils/formatters';

import type {
  BrandProfile,
  Campaign,
  Deliverable,
  DeliverableGenericTimeline,
  IndividualOfferCampaignInfo,
  IndividualOfferCreatorProfileInfo,
  IndividualOfferInfo,
  IndividualOfferPayload,
  IndividualOfferTaskInfoTimeline,
  Subcampaign,
  ThunkAction
} from 'types';
import type { ResponseType } from 'axios';

type PayloadProps = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  brandProfile: BrandProfile,
  offerDeliverables: Array<Deliverable>,
  acceptedAt: string | undefined | null,
  isForBrand: boolean,
  variablePaymentsAmount: number
};

const getTimeline = (
  input: DeliverableGenericTimeline
): IndividualOfferTaskInfoTimeline => ({
  endDate: getDisplayDate(input.endDate),
  startDate: getDisplayDate(input.startDate)
});

async function getPayload(
  {
    campaign,
    subcampaign,
    brandProfile,
    offerDeliverables,
    acceptedAt,
    isForBrand,
    variablePaymentsAmount
  }: PayloadProps
): Promise<IndividualOfferPayload> {
  const offer = (subcampaign.updatedOffer) ? subcampaign.updatedOffer : subcampaign.offer;

  const offerInfo: IndividualOfferInfo = {
    budget: offer.budget,
    variablePaymentsAmount,
    includesShipping: offer.includesShipping,
    perks: offer.perks,
    legalRequirements: offer.legalRequirements,
    additionalRequirements: campaign.additionalRequirementsTemplate,
    state: (subcampaign.updatedOffer) ? 're-opened' : offer.state,
    type: offer.type,
    acceptedAt: acceptedAt ? getDisplayDate(acceptedAt) : undefined,
    tasks: sortDeliverables(offerDeliverables).map((deliverable) => ({
      type: deliverable.deliverableType,
      links: deliverable.links,
      name: deliverable.name,
      description: deliverable.description,
      tags: deliverable.tags,
      hashtags: deliverable.hashtags,
      mentions: deliverable.mentions,
      location: deliverable.location,
      startDate: getDisplayDate(deliverable.startDate),
      endDate: getDisplayDate(deliverable.endDate),
      contentApprovalV2: !!deliverable.contentApprovalV2,
      contentGoLiveTimeline: deliverable.contentApprovalV2
        ? getTimeline(deliverable.contentGoLiveTimeline)
        : undefined,
      contentApprovalTimeline: !!deliverable.contentApprovalV2 && !!deliverable.requiresApproval
        ? getTimeline(deliverable.contentApprovalTimeline)
        : undefined,
      insightsProvisionTimeline: deliverable.contentApprovalV2
        ? getTimeline(deliverable.insightsProvisionTimeline)
        : undefined
    }))
  };

  const mainImageUrl = campaign.mainImage
    && campaign.mainImage.url
    && getCloudinaryImageUrl({ url: campaign.mainImage.url },
      {
        width: 110,
        height: 110,
        preserveAspectRatio: false
      });

  const assetPreviews = await Promise.all(getCampaignAssetsPreviews(campaign.assets || []));

  const campaignInfo: IndividualOfferCampaignInfo = {
    agency: brandProfile.managedBy?.name,
    brand: brandProfile.name,
    name: campaign.name,
    description: campaign.description,
    startDate: getDisplayDate(campaign.startDate),
    endDate: getDisplayDate(campaign.endDate),
    mainImageUrl,
    managers: (campaign.managers || []).map(m => m.name),
    assets: assetPreviews
      .map(url => getCloudinaryImageUrl({ url }, { width: 135, height: 135 }))
  };

  const channels = subcampaign.creatorProfile.channels;

  const creatorProfile: IndividualOfferCreatorProfileInfo = {
    name: subcampaign.creatorProfile.name,
    firstName: subcampaign.creatorProfile.influencerFirstName,
    lastName: subcampaign.creatorProfile.influencerLastName,
    profileImage: subcampaign.creatorProfile.profileImage
      ? getCloudinaryImageUrl(subcampaign.creatorProfile.profileImage, { width: 100, height: 100 })
      : null,
    channels: ['instagram', 'youtube'].reduce((list, channelType) => {
      const metrics = channels[channelType] ? channels[channelType].metrics : null;
      if (metrics) {
        list.push({
          type: channelType as 'instagram' | 'youtube',
          engagement: getFormattedPercentageRate(_get(metrics, 'engagement')),
          followers: getFormattedFollowersValue(getNumFollowers(_get(metrics, 'followers') || 0))
        });
      }

      return list;
    }, [] as Array<{
      type: 'instagram' | 'youtube',
      engagement: string,
      followers: string
    }>)
  };

  return {
    offer: offerInfo,
    campaign: campaignInfo,
    creatorProfile,
    viewAs: isForBrand ? 'brand' : 'influencer'
  };
}

async function getAcceptedAt(organizationId: number | undefined | null, subcampaign: Subcampaign): Promise<string | undefined | null> {
  if (!organizationId) {
    return null;
  }
  try {
    const historyResponse = await get(`${API.END_POINTS.INFLUENCERS.getInfluencerCampaignHistory(organizationId, subcampaign.creatorProfile.id)}?filter_by=brand`);
    const campaignHistory = historyResponse.data.historicalData.find(h => h.subcampaign.id === subcampaign.id);

    if (!campaignHistory) {
      return null;
    }

    const event = campaignHistory.negotiationHistory.find(h => h.offerState === 'accepted' && h.historyType === 'agreement_change');

    if (!event) {
      return null;
    }

    return event.date;
  } catch (e) {
    logError(e);
  }

  return null;
}

export function downloadIndividualOffer(subcampaignId: number, isForBrand: boolean): ThunkAction {
  const url = API.END_POINTS.PDFKITTY.generateIndividualOfferPDF();

  return async function downloadOfferAsPDF(dispatch, getState) {
    dispatch(showLoading(url));

    const requestConfig: { responseType: ResponseType } = {
      responseType: 'blob'
    };

    try {
      const subcampaignResponse = await get(API.END_POINTS.SUBCAMPAIGNS.getSubcampaign(subcampaignId));
      const subcampaign = subcampaignResponse.data;
      const [campaignResponse, brandProfileResponse, offerDeliverablesResponse, variablePaymentsResponse] = await Promise.all([
        get(`${API.END_POINTS.CAMPAIGNS.getCampaign(subcampaign.campaign.id)}?include=main_image,assets,managers`),
        get(API.END_POINTS.BRANDS.getBrandProfile(subcampaign.campaign.brandProfileId)),
        get(API.END_POINTS.OFFERS.getOfferDeliverables(subcampaign.offer.id)),
        get(API.END_POINTS.VARIABLE_PAYMENTS.getSubcampaignVariablePayments(subcampaign.campaign.id, subcampaignId))
      ]);
      const campaign = campaignResponse.data;
      const brandProfile = brandProfileResponse.data;
      const offerDeliverables = offerDeliverablesResponse.data;
      const variablePayments = variablePaymentsResponse.data || {};
      const variablePaymentsAmount = variablePayments.totalAmount;
      const fileName = `Collabary - Individual Offer - ${encodeFileNameInZipArchive(campaign.name)} - ${encodeFileNameInZipArchive(subcampaign.creatorProfile.name)}.pdf`;

      let acceptedAt: string | null | undefined = null;

      if (isForBrand && isAccepted(subcampaign) && !isNegotiationsReopened(subcampaign)) {
        const organizationId = getState().organizations.currentOrganizationId;
        acceptedAt = await getAcceptedAt(organizationId, subcampaign);
      }

      const data = await getPayload({
        campaign,
        subcampaign,
        brandProfile,
        offerDeliverables,
        acceptedAt,
        isForBrand,
        variablePaymentsAmount
      });

      const response = await post(url,
        {
          data,
          ...requestConfig
        });

      downloadFile(response.data, fileName);
      dispatch(hideLoading(url));
    } catch (e) {
      dispatch(hideLoading(url));
      logError(e);
    }

    dispatch(hideLoading(url));
  };
}
